// @flow

/* -------------------- Globals ----------------*/
import produce from 'immer';

/* -------------------- Third party --------------- */
import find from 'lodash/find';
import findLast from 'lodash/findLast';

/* -------------------- Action Types ----------------*/
import {
  ANSWER,
  BACK,
  TOGGLE_CHECK_BOX,
  COMPLETE_SESSION,
  NEXT,
  SELECT,
  SKIP,
  SHOW,
  START_SESSION,
  STOP_SESSION,
  START_NEXT_BATCH,
  REVIEW,
  SET_TASK_IDS,
} from './actionTypes';
import { RESET_APP } from '../actionTypes';

/* -------------------- Constants --------------- */
import { initialState } from './constants';
import {
  THRESHOLD_MASTERY_NORMAL_SESSION,
} from '../session/constants';
/* -------------------- Types ----------------*/
import type {
  ChoiceEventType,
  ChoiceStatuses,
  MultiChoiceState,
  Question,
  SelectionState,
  Distribution,
  Variant,
} from './types';
import {
  type LearningOrders,
} from '../types';
import {
  answeredState,
  choiceEventType,
  selectionState,
  checkBoxStatus,
} from './types';

/* -------------------- Helpers ----------------*/
import {
  setTarget,
  skipQuestion,
  addAnswer,
} from '../functions/progressReducer';
import {
  countQuestions,
} from '../functions/distributionReducer';
import { summarizeGame } from '../functions/summary';
import calcScore from '../functions/scoring/multiChoice';

/* -------------------- Utils ----------------*/
import { isEmpty, isEmptyList, timeStamp } from '../simpleUtils';

/* ----------------- Helpers ---------------------*/
const initializeChoiceStatuses = (
  state: MultiChoiceState,
): ChoiceStatuses => {
  const { currentIndex, questions } = state;
  const { choices } = questions[currentIndex];
  const statuses = ({}: ChoiceStatuses);
  const initialStatuses = (choices || []).reduce(
    (obj, { index }: { index: string }) => {
      obj[`${index}`] = {
        selectionStatus: selectionState.unselected,
        answeredStatus: answeredState.none,
      };
      return obj;
    }, statuses,
  );
  return initialStatuses;
};

const evaluateAnswerStatus = (
  index: number,
  state: MultiChoiceState,
) => {
  const { questions, answers } = state;
  if (index >= questions.length) {
    return false;
  }
  const question = questions[index];
  return answers.some(a => a.questionId === question.id);
};

const createNewEvent = (
  currentIndex: number,
  selectedChoiceId: ?string,
  state: MultiChoiceState,
  eventType: ChoiceEventType,
) => {
  const {
    questions,
    sessionId,
    inReverseMode,
  } = state;
  if (sessionId) {
    const {
      id,
      choices,
      contentId,
    } = questions[currentIndex];

    const eventContext = {
      sessionId,
      contextId: contentId,
      time: new Date().toISOString(),
      timeTaken: 0,
    };

    const choicesIds = choices.map(choice => `${choice.index}`);
    const eventPayload = {
      questionId: id,
      selectionId: selectedChoiceId,
      choices: choicesIds,
      inReverseMode,
    };

    const choiceEvent = {
      eventContext,
      eventType,
      eventPayload,
    };
    return choiceEvent;
  } return null;
};

const handleShow = (state: MultiChoiceState) => produce(state, (draft) => {
  const {
    currentIndex,
    startTime,
  } = state;
  const newEvent = createNewEvent(
    currentIndex,
    null,
    state,
    choiceEventType.SHOW,
  );
  if (!startTime) {
    const newStartTime = new Date().toISOString();
    draft.startTime = newStartTime;
  }
  if (newEvent) {
    draft.events = [...draft.events, newEvent];
    const { eventContext: { contextId } } = newEvent;
    draft.contextId = contextId;
  }
});

const persistState = (
  state: MultiChoiceState,
  currentIndex: number,
) => produce(state, (draft) => {
  const { answers, questions, progressData: { target } } = state;
  const currentQuestionAnswered = evaluateAnswerStatus(currentIndex, state);
  const currentQuestion = questions[currentIndex];
  const previouslyAnsweredQuestion = find(
    answers,
    answer => answer.questionId === currentQuestion.id,
  );
  draft.choiceStatuses = previouslyAnsweredQuestion
    ? previouslyAnsweredQuestion.choiceStatuses
    : initializeChoiceStatuses({ ...state, currentIndex });
  draft.currentIndex = currentIndex;
  draft.currentQuestionAnswered = currentQuestionAnswered;
  draft.selectedChoiceIds = [];
  const isLastQuestionVisible = currentIndex === target.length - 1;
  draft.isLastQuestionVisible = isLastQuestionVisible;
});

/* -------------------- Handlers ----------------*/
const handleChoiceSelection = (
  state: MultiChoiceState,
  selectedChoiceId: string,
  selection: SelectionState,
) => produce(state, (draft) => {
  const {
    timesChanged,
    questions,
  } = state;
  const question = questions[draft.currentIndex];

  const isSata = question.variant === 'SATA';
  const choiceStatuses = Object.keys(state.choiceStatuses).length === 0
    ? initializeChoiceStatuses(state)
    : state.choiceStatuses;
  const currentChoiceStatuses = choiceStatuses[selectedChoiceId];

  const filteredCrossedOutChoices = Object.keys(choiceStatuses).filter(item => {
    const { selectionStatus } = choiceStatuses[item];
    return selectionStatus === selectionState.crossedOut;
  }).reduce((a, c) => {
    a[c] = choiceStatuses[c];
    return a;
  }, {});
  const getNewSelection = () => {
    if (selection === selectionState.crossedOut) {
      return currentChoiceStatuses?.selectionStatus === selectionState.crossedOut
        ? selectionState.unselected
        : selectionState.crossedOut;
    } if (currentChoiceStatuses?.selectionStatus) {
      return currentChoiceStatuses.selectionStatus === selectionState.selected
        ? selectionState.unselected
        : selectionState.selected;
    }
    return selectionState.selected;
  };
  const newSelection = getNewSelection();

  const newChoiceStatuses = {
    ...(isSata
      ? choiceStatuses : {}),
    ...(filteredCrossedOutChoices || {}),
    [selectedChoiceId]: {
      selectionStatus: newSelection,
      answeredStatus: currentChoiceStatuses
        ? currentChoiceStatuses.answeredStatus : answeredState.none,
    },
  };
  const selectedChoiceIds = Object.keys(newChoiceStatuses)
    .filter(key => newChoiceStatuses[key].selectionStatus === selectionState.selected);
  draft.choiceStatuses = newChoiceStatuses;
  draft.selectedChoiceIds = selectedChoiceIds;
  const newEvent = createNewEvent(
    draft.currentIndex,
    selectedChoiceId,
    draft,
    choiceEventType.SELECT,
  );
  if (newEvent) {
    draft.events = [...draft.events, newEvent];
    const {
      eventContext: {
        contextId,
      },
    } = newEvent;
    draft.contextId = contextId;
  }

  const showEvent = findLast(draft.events,
    event => event.eventType === choiceEventType.SHOW
      && event.eventPayload.questionId === question.id);
  const {
    eventContext: {
      time,
    },
  } = showEvent;

  const endTime = timeStamp();
  const startTime = new Date(time).getTime();
  const timeTaken = Math.min((endTime - startTime) / 1000, 300);
  const existingId = timesChanged.findIndex(item => item.id === selectedChoiceId);
  if (existingId > -1) timesChanged[existingId].timeTaken.push(timeTaken);
  const newTimesChanged = existingId > -1 ? timesChanged
    : [...timesChanged, { id: selectedChoiceId, timeTaken: [timeTaken] }];
  draft.timesChanged = newTimesChanged;
});

const handleAnswer = (
  state: MultiChoiceState,
  data: *,
  variant: Variant,
) => produce(state, (draft) => {
  const {
    currentIndex,
    progressData,
    lastSeenIndex,
    questions,
    selectedChoiceIds,
    currentQuestionAnswered,
    events,
    uniqueAnswers,
    answeredFacts,
    sessionMeta,
  } = state;
  if (selectedChoiceIds?.length === 0
    || currentQuestionAnswered
  ) {
    return;
  }

  const question = questions[currentIndex];
  const {
    currentScore,
    contentId,
    factId,
    variant: itemVariant,
  } = question;
  draft.progressData = addAnswer(
    progressData,
    question.id,
  );
  const { confidence } = data;
  const { answerId, choices } = question;
  const correctChoiceIds = choices.filter(({
    isCorrect,
  }) => isCorrect).map(item => item.index);
  const isCorrect = selectedChoiceIds?.every(item => correctChoiceIds.includes(item));
  const crossedoutChoices = Object.keys(state.choiceStatuses).filter(item => {
    const { selectionStatus } = state.choiceStatuses[item];
    return selectionStatus === selectionState.crossedOut;
  });

  const newChoiceStatuses = initializeChoiceStatuses(state);
  choices?.forEach(({ isCorrect: choiceCorrect, index }) => {
    if (choiceCorrect) {
      newChoiceStatuses[index].answeredStatus = answeredState.correct;
    } else if (selectedChoiceIds.includes(index)) {
      newChoiceStatuses[index].answeredStatus = answeredState.incorrect;
    } else if (crossedoutChoices?.includes(index)) {
      newChoiceStatuses[index].selectionStatus = selectionState.crossedOut;
    }
  });

  const endTime = timeStamp();
  const showEvent = findLast(events,
    event => event.eventType === choiceEventType.SHOW
      && event.eventPayload.questionId === question.id);
  const {
    eventContext: {
      time,
    },
  } = showEvent;
  const startTime = new Date(time).getTime();
  const timeTaken = Math.min((endTime - startTime) / 1000, 300);
  const {
    score, timeFactor,
  } = calcScore(confidence, isCorrect, timeTaken, question, sessionMeta, variant);

  const newSessionMeta = { ...sessionMeta };
  if (sessionMeta[question.factId]) {
    newSessionMeta[question.factId] = {
      lastTime: endTime,
      numSuccessAttempts: isCorrect ? sessionMeta[question.factId].numSuccessAttempts + 1 : 0,
      numTotalAttempts: (sessionMeta[question.factId].numTotalAttempts + 1),
    };
  } else {
    newSessionMeta[question.factId] = {
      lastTime: endTime,
      numSuccessAttempts: isCorrect ? 1 : 0,
      numTotalAttempts: 1,
    };
  }

  draft.sessionMeta = newSessionMeta;

  const previousScoreValue = currentScore ? currentScore.value : undefined;
  const currentAnsweredFact = {
    previousScore: previousScoreValue,
    currentScore: score,
    termId: question.termId || '',
    studySetId: contentId,
  };
  if (factId) answeredFacts[factId] = currentAnsweredFact;
  const thresold = THRESHOLD_MASTERY_NORMAL_SESSION;

  const previouslyHigh = previousScoreValue
    ? previousScoreValue >= thresold : false;
  const currentlyHigh = score >= thresold;
  if (factId) {
    const isItemAnswered = uniqueAnswers[factId];

    if (!isItemAnswered) {
      const newuniqueAnswerItem = {
        score,
        high: currentlyHigh,
        previouslyHigh,
        contentId,
      };
      uniqueAnswers[factId] = newuniqueAnswerItem;
    } else if (
      uniqueAnswers && isItemAnswered
    ) {
      uniqueAnswers[factId] = {
        score,
        high: currentlyHigh,
        previouslyHigh,
        contentId,
      };
    }
  }
  const answerItem = {
    factId: question.factId,
    termId: question.termId,
    questionId: question.id,
    selectedChoiceIds,
    confidence,
    choiceStatuses: newChoiceStatuses,
    answerStatus: isCorrect,
    correctChoiceId: answerId,
    score,
    previousScore: previousScoreValue,
    timeTaken,
    timeFactor,
    contentId,
    variant: itemVariant,
  };
  draft.answeredFacts = answeredFacts;
  draft.answers = [...draft.answers, answerItem];
  draft.choiceStatuses = newChoiceStatuses;
  draft.currentQuestionAnswered = true;

  let newLastSeenIndex = lastSeenIndex;
  if (lastSeenIndex < currentIndex + 1) {
    newLastSeenIndex = currentIndex + 1;
  }
  draft.lastSeenIndex = newLastSeenIndex;
  const newEvent = createNewEvent(
    currentIndex,
    selectedChoiceIds[0],
    draft,
    choiceEventType.ANSWER,
  );
  if (newEvent) {
    draft.events = [...draft.events, newEvent];
    const {
      eventContext: {
        contextId,
      },
    } = newEvent;
    draft.contextId = contextId;
  }
  draft.uniqueAnswers = uniqueAnswers;
});
const handleBack = (state: MultiChoiceState) => {
  const { currentIndex } = state;
  return persistState(state, currentIndex - 1);
};

const handleNext = (state: MultiChoiceState) => {
  const {
    lastSeenIndex, currentIndex, selectedChoiceIds, progressData: { target },
    questions,
  } = state;
  const newIndex = questions[currentIndex + 1] ? currentIndex + 1 : currentIndex;
  const currentQuestionAnswered = evaluateAnswerStatus(
    newIndex,
    state,
  );

  if (currentQuestionAnswered) {
    return persistState(state, newIndex);
  }
  return produce(state, (draft) => {
    const newEvent = createNewEvent(
      currentIndex,
      selectedChoiceIds[0],
      draft,
      choiceEventType.NEXT,
    );

    if (newEvent) {
      draft.events = [...draft.events, newEvent];
      const {
        eventContext: {
          contextId,
        },
      } = newEvent;
      draft.contextId = contextId;
    }
    draft.currentQuestionAnswered = currentQuestionAnswered;
    let newLastSeenIndex = lastSeenIndex;
    if (lastSeenIndex < newIndex) {
      newLastSeenIndex = newIndex;
    }
    draft.currentIndex = newIndex;
    draft.choiceStatuses = initializeChoiceStatuses({
      ...state,
      currentIndex: newIndex,
    });
    draft.selectedChoiceIds = [];
    draft.timesChanged = [];
    draft.lastSeenIndex = newLastSeenIndex;
    const isLastQuestionVisible = newIndex === target.length - 1;
    draft.isLastQuestionVisible = isLastQuestionVisible;
  });
};

const handleSkip = (state: MultiChoiceState) => {
  const {
    currentIndex,
    progressData,
    questions,
    progressData: { target },
  } = state;
  let currentQuestionAnswered = evaluateAnswerStatus(
    currentIndex,
    state,
  );

  const newIndex = questions[currentIndex + 1]
    ? currentIndex + 1
    : currentIndex;
  currentQuestionAnswered = evaluateAnswerStatus(
    newIndex,
    state,
  );
  if (currentQuestionAnswered) {
    return persistState(state, newIndex);
  }
  return produce(state, (draft) => {
    const newEvent = createNewEvent(
      currentIndex,
      null,
      draft,
      choiceEventType.SKIP,
    );
    if (newEvent) {
      draft.events = [...draft.events, newEvent];
      const {
        eventContext: {
          contextId,
        },
      } = newEvent;
      draft.contextId = contextId;
    }
    draft.currentQuestionAnswered = currentQuestionAnswered;

    const currentQuestionId = questions[currentIndex].id;
    const newProgressData = skipQuestion(
      progressData,
      currentQuestionId,
    );
    draft.progressData = newProgressData;

    draft.currentIndex = newIndex;
    draft.choiceStatuses = initializeChoiceStatuses(
      { ...state, currentIndex: newIndex },
    );
    draft.selectedChoiceIds = [];
    const isLastQuestionVisible = newIndex === target.length - 1;
    draft.isLastQuestionVisible = isLastQuestionVisible;
  });
};

const summarize = (
  state: MultiChoiceState,
  data: *,
) => produce(state, (draft) => {
  const {
    answers,
    startTime,
    endTime,
  } = state;
  const scores = answers.map(({ score }) => score);
  const summary = summarizeGame(
    scores,
    startTime,
    endTime,
  );
  draft.summary = summary;

  if (data) {
    draft.reviewedContents = data[0].sessionTags;
    draft.isEndSessionInProgress = false;
  } else {
    draft.isEndSessionInProgress = true;
  }
  draft.isSessionComplete = true;
});

const handleEndSession = (
  state: MultiChoiceState,
  data: *,
) => {
  const { currentIndex, selectedChoiceIds } = state;
  const newEvent = createNewEvent(currentIndex,
    selectedChoiceIds[0], state, choiceEventType.TERMINATE);
  const endTime = new Date().toISOString();
  const events = newEvent
    ? [...state.events, newEvent]
    : state.events;
  return summarize({
    ...state,
    endTime,
    events,
  }, data);
};
const handleSessionComplete = (
  state: MultiChoiceState,
  data: *,
) => {
  const { currentIndex } = state;
  const newEvent = createNewEvent(
    currentIndex === 0 ? currentIndex : currentIndex - 1,
    null,
    state,
    choiceEventType.COMPLETE,
  );
  const endTime = new Date().toISOString();
  const events = newEvent ? [...state.events, newEvent] : state.events;
  return summarize({
    ...state,
    endTime,
    events,
  }, data);
};
const handleLoadingSession = (state: MultiChoiceState) => produce(state, (draft) => {
  draft.sessionLoading = true;
});

const handleReset = state => produce(state, (draft) => {
  draft.isSessionComplete = false;
  draft.isLastQuestionVisible = false;
  draft.contextId = null;
  draft.endTime = null;
  draft.uniqueAnswers = { };
  draft.currentIndex = 0;
  draft.isEndSessionInProgress = false;
  draft.currentQuestionAnswered = false;
  draft.selectedChoiceIds = [];
  draft.currentIndex = 0;
  draft.inReviewMode = false;
  draft.requestedQuestionsCount = 0;
  draft.answeredFacts = { };
  draft.reviewQuestions = [];
  draft.progressData = {
    target: [],
    answered: [],
    skipped: [],
  };
  draft.choiceStatuses = { };
  draft.answers = [];
  draft.events = [];
  draft.questions = [];
  draft.sessionCreated = false;
  draft.sessionLoading = false;
  draft.reviewedContents = [];
  draft.hasMore = false;
  draft.addedTaskIds = [];
});

type StartSessionData = {
  questions: Question[],
  id: string,
  distribution: Distribution<number>,
  contextId: string,
  inReverseMode: ?boolean,
  learningOrder: LearningOrders,
  hasMore: boolean,
  scheduleId: string,
  numGoal: number
}

const handleStartSession = (
  state: MultiChoiceState,
  data: StartSessionData,
) => produce(state, (draft) => {
  const {
    questions,
    id,
    inReverseMode,
    learningOrder,
    distribution,
    hasMore,
    scheduleId,
    numGoal,
  } = data;

  const questionsCount = !isEmpty(scheduleId) ? numGoal : countQuestions(distribution);
  const ids = questions.map(q => q.id);
  const { contentId } = questions[0];
  draft.hasMore = hasMore;
  draft.scheduleId = scheduleId;
  draft.progressData = setTarget(ids);
  draft.questions = questions;
  draft.requestedQuestionsCount = questionsCount;
  draft.inReverseMode = inReverseMode;
  draft.sessionId = id;
  draft.contextId = contentId;
  draft.currentIndex = 0;
  draft.learningOrder = learningOrder;
  draft.choiceStatuses = initializeChoiceStatuses(draft);
  draft.sessionCreated = true;
  draft.sessionLoading = false;
});

const handleNextBatch = (
  state: MultiChoiceState,
  data: StartSessionData,
) => produce(state, (draft) => {
  const {
    questions: nextBatchQuestions,
    hasMore,
  } = data;
  const {
    questions: existingQuestions,
    progressData: {
      skipped,
      answered,
    },
  } = state;

  const totalQuestions = [...existingQuestions, ...nextBatchQuestions];

  const ids = totalQuestions.map(q => q.id);
  draft.hasMore = hasMore;
  draft.progressData = setTarget(
    ids,
    answered,
    skipped,
  );
  draft.questions = totalQuestions;
  draft.isLastQuestionVisible = !!isEmpty(nextBatchQuestions);
  draft.requestedQuestionsCount = totalQuestions.length;
});

const handleToggleCheckbox = (state: MultiChoiceState,
  key: string) => produce(state, (draft) => {
  const {
    reviewCheckBoxStatus,
  } = state;
  const newCheckBoxStatus = {
    [key]: reviewCheckBoxStatus[key] === checkBoxStatus.checked
      ? checkBoxStatus.unchecked : checkBoxStatus.checked,
    ...reviewCheckBoxStatus,
  };
  draft.reviewCheckBoxStatus = newCheckBoxStatus;
});

const handleSetTaskIds = (state: MultiChoiceState,
  id: string) => produce(state, (draft) => {
  const {
    addedTaskIds,
  } = state;
  if (!addedTaskIds.includes(id)) draft.addedTaskIds = [...addedTaskIds, id];
});

const handleReview = (
  state: MultiChoiceState,
  data: *,
) => produce(state, (draft) => {
  const {
    questions,
    answers,
  } = data;
  const {
    reviewQuestions,
    questions: oldQuestions,
  } = state;
  if (isEmptyList(reviewQuestions)) {
    draft.reviewQuestions = oldQuestions;
  }
  draft.questions = questions;
  draft.answers = answers;
  draft.currentIndex = 0;
  const currentQuestion = questions[0];

  const previouslyAnsweredQuestion = find(
    answers,
    answer => answer.questionId === currentQuestion.id,
  );

  draft.inReviewMode = true;
  draft.choiceStatuses = previouslyAnsweredQuestion.choiceStatuses;
  draft.currentQuestionAnswered = true;
  draft.isLastQuestionVisible = questions.length === 1;
});

const multichoiceReducer = (state: MultiChoiceState = { ...initialState }, action: *) => {
  switch (action.type) {
    case START_SESSION:
      if (action.sessionLoading) {
        return handleLoadingSession(state);
      }
      return handleStartSession(state, action.data);
    case SELECT:
      return handleChoiceSelection(state, action.data.selectedChoiceId, action.data.selection);
    case ANSWER:
      return handleAnswer(state, action.data, action.variant);
    case NEXT:
      return handleNext(state);
    case SHOW:
      return handleShow(state);
    case SKIP:
      return handleSkip(state);
    case BACK:
      return handleBack(state);
    case COMPLETE_SESSION:
      return handleSessionComplete(state,
        action.data);
    case RESET_APP:
      return handleReset(state);
    case STOP_SESSION:
      return handleEndSession(state,
        action.data);
    case TOGGLE_CHECK_BOX:
      return handleToggleCheckbox(state, action.data);
    case START_NEXT_BATCH:
      return handleNextBatch(state,
        action.data);
    case REVIEW:
      return handleReview(
        state,
        action.data,
      );
    case SET_TASK_IDS:
      return handleSetTaskIds(
        state,
        action.data,
      );
    default:
      return state;
  }
};

export default multichoiceReducer;
